import { FormEvent, useCallback, useState } from 'react';
import {
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';

import { useCheckCondition } from '../../hooks/useCheckCondition';

import { LuCheck, LuX } from 'react-icons/lu';
import { useEvaluate } from '../../hooks/useEvaluate';
import { Value } from '@/types/global';

export const ConditionChecker = () => {
  const [condition, setCondition] = useState('');
  const [conditionTrue, setConditionTrue] = useState<boolean | undefined>();

  const checkCondition = useCheckCondition();

  const onCheckCondition = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      setConditionTrue(checkCondition(condition));
    },
    [condition, checkCondition],
  );

  return (
    <AccordionItem
      defaultOpen
      className="bg-[var(--dbg-bg-secondary)] text-[var(--dbg-text)] border-none"
    >
      <AccordionTrigger className="px-4 text-[var(--dbg-text)] [&>svg]:text-[var(--dbg-text-secondary)]">
        Condition Checker
      </AccordionTrigger>
      <AccordionContent className="px-4">
        <form
          className="flex flex-row justify-start items-center gap-6 pb-6"
          onSubmit={onCheckCondition}
        >
          <textarea
            className="flex-1 bg-[var(--dbg-bg-code)] text-[var(--dbg-text)] border border-[var(--dbg-border)] rounded px-3 py-2 text-sm focus:outline-none focus:border-[var(--dbg-accent)] placeholder:text-[var(--dbg-text-secondary)]"
            placeholder="Condition"
            value={condition}
            onChange={(e) => setCondition(e.target.value)}
          />
          <button
            type="submit"
            className="px-4 py-2 text-sm border border-[var(--dbg-border)] text-[var(--dbg-accent)] rounded hover:bg-[var(--dbg-hover)] whitespace-nowrap cursor-pointer"
          >
            Check
          </button>
          {conditionTrue !== undefined && (
            <Badge
              variant="outline"
              className={
                conditionTrue
                  ? 'border border-[#065f46] bg-[#064e3b] text-[#6ee7b7] [&>svg]:text-[#6ee7b7]'
                  : 'border border-[#7f1d1d] bg-[#450a0a] text-[#fca5a5] [&>svg]:text-[#fca5a5]'
              }
            >
              {conditionTrue ? (
                <>
                  <LuCheck className="size-3" />
                  Condition true
                </>
              ) : (
                <>
                  <LuX className="size-3" />
                  Condition false
                </>
              )}
            </Badge>
          )}
        </form>
      </AccordionContent>
    </AccordionItem>
  );
};

export const Evaluator = () => {
  const [clause, setClause] = useState('');
  const [result, setResult] = useState<Value>();

  const evaluate = useEvaluate();

  const onCheckCondition = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      setResult(evaluate(clause));
    },
    [clause, evaluate],
  );

  return (
    <AccordionItem
      defaultOpen
      className="bg-[var(--dbg-bg-secondary)] text-[var(--dbg-text)] border-none"
    >
      <AccordionTrigger className="px-4 text-[var(--dbg-text)] [&>svg]:text-[var(--dbg-text-secondary)]">
        Expression Evaluator
      </AccordionTrigger>
      <AccordionContent className="px-4">
        <form
          className="flex flex-row justify-start items-center gap-6 pb-6"
          onSubmit={onCheckCondition}
        >
          <textarea
            className="flex-1 bg-[var(--dbg-bg-code)] text-[var(--dbg-text)] border border-[var(--dbg-border)] rounded px-3 py-2 text-sm focus:outline-none focus:border-[var(--dbg-accent)] placeholder:text-[var(--dbg-text-secondary)]"
            placeholder="Expression"
            value={clause}
            onChange={(e) => setClause(e.target.value)}
          />
          <button
            type="submit"
            className="px-4 py-2 text-sm border border-[var(--dbg-border)] text-[var(--dbg-accent)] rounded hover:bg-[var(--dbg-hover)] whitespace-nowrap cursor-pointer"
          >
            Check
          </button>
        </form>
        <input
          disabled
          value={result !== undefined ? String(result) : ''}
          className="w-full bg-[var(--dbg-bg)] text-[var(--dbg-text-secondary)] border border-[var(--dbg-border)] rounded px-3 py-2 text-sm"
        />
      </AccordionContent>
    </AccordionItem>
  );
};
